import { decodeJwtPayload } from './decodeJwtPayload';
import { isInIframe } from './iframeCommunication';

/**
 * Where the guest API bearer token comes from.
 *
 * A real guest session is persisted by the invite flow in `localStorage.weddingToken`.
 * The portal's visual preview uses a portal preview JWT instead. That token must never be
 * persisted as `weddingToken`: it would be mistaken for the couple's own session on the live
 * gallery (and is refused for guest-identity writes, #370). It is held in memory for the
 * lifetime of the preview session instead, and `getGuestAuthToken()` hands the right token to
 * every API call.
 *
 * A reload of the preview iframe (Venue "Try Again", a service-worker update, a manual reload)
 * drops that memory and the bootstrap `?token=`, so the preview token is also mirrored to
 * `sessionStorage` together with the gallery it belongs to. The mirror is tab-scoped (never shared
 * with the couple's own tab) and is only ever restored inside an iframe, for the same gallery, so
 * a reloaded preview frame stays a preview instead of degrading into the couple's guest session.
 */

export const GUEST_TOKEN_STORAGE_KEY = 'weddingToken';
export const PREVIEW_SESSION_STORAGE_KEY = 'weddingPreviewSession';

let previewSessionToken = '';

function normalizeGalleryId(galleryId) {
  return typeof galleryId === 'string' ? galleryId.trim() : '';
}

/**
 * Gallery id of the current `/g/:galleryId` route, or '' outside a gallery route.
 *
 * This duplicates the route shape declared in `App.js` (`<Route path="/g/:galleryId" element={<GalleryLayout />}>`,
 * rendered by a `BrowserRouter` with no basename) because the upload queue and `invalidateGuestSession`
 * run outside the router. If that route or a router basename ever changes, change this with it: a
 * mismatch silently stops the mirror from matching and a reloaded preview frame falls back to `weddingToken`.
 */
function currentRouteGalleryId() {
  const match = window.location.pathname.match(/^\/g\/([^/?#]+)/);
  if (!match) {
    return '';
  }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/** True for a portal preview JWT (`purpose: 'preview'`); signature is not checked client-side. */
export function isPreviewToken(token) {
  return decodeJwtPayload(token)?.purpose === 'preview';
}

/**
 * Starts (or refreshes) the preview session: the token lives in memory and, when the gallery it
 * belongs to is known and the app runs inside an iframe, is mirrored to `sessionStorage` so an
 * iframe reload can restore it. A top-level tab keeps the token in memory only (it never restores
 * the mirror, so there is nothing to mirror for). It is never written to `localStorage`.
 * @param {string} token
 * @param {string} [galleryId] gallery the token was authorized for (the route id of the preview)
 */
export function setPreviewSessionToken(token, galleryId) {
  previewSessionToken = typeof token === 'string' ? token : '';
  const mirroredGalleryId = normalizeGalleryId(galleryId);
  if (!previewSessionToken || !mirroredGalleryId || !isInIframe()) {
    return;
  }
  try {
    sessionStorage.setItem(
      PREVIEW_SESSION_STORAGE_KEY,
      JSON.stringify({ galleryId: mirroredGalleryId, token: previewSessionToken })
    );
  } catch {
    // Storage unavailable: the preview still works until the frame reloads.
  }
}

/**
 * Drops the in-memory preview token only. The `sessionStorage` mirror is kept on purpose: when a
 * preview token is rejected the reloaded frame must still know it is a preview, so it reports the
 * preview as unavailable instead of falling back to the couple's own guest session.
 */
export function clearPreviewSessionToken() {
  previewSessionToken = '';
}

/** Removes the tab-scoped `sessionStorage` mirror of the preview session. */
export function discardStoredPreviewSession() {
  try {
    sessionStorage.removeItem(PREVIEW_SESSION_STORAGE_KEY);
  } catch {
    // Nothing to discard when storage is unavailable.
  }
}

/**
 * The mirrored preview token a reloaded preview iframe may resume. Restored only inside an iframe
 * (a top-level tab ignores it) and only for the gallery it was mirrored for, and only if it is a
 * preview-purpose JWT. The token is a candidate: the server still has to authorize it before it is
 * used for the session.
 * @param {string} galleryId gallery of the current route
 * @returns {string} the preview token, or '' when there is nothing to restore
 */
export function getRestorablePreviewToken(galleryId) {
  const expectedGalleryId = normalizeGalleryId(galleryId);
  if (!expectedGalleryId || !isInIframe()) {
    return '';
  }
  try {
    const stored = JSON.parse(sessionStorage.getItem(PREVIEW_SESSION_STORAGE_KEY));
    if (
      stored &&
      typeof stored === 'object' &&
      stored.galleryId === expectedGalleryId &&
      typeof stored.token === 'string' &&
      isPreviewToken(stored.token)
    ) {
      return stored.token;
    }
  } catch {
    // Unreadable mirror: treat as nothing to restore.
  }
  return '';
}

/**
 * The preview token this frame is running under: the in-memory token of an authorized preview
 * session or, right after an iframe reload, the mirrored token for the current gallery route.
 */
function activePreviewToken() {
  return previewSessionToken || getRestorablePreviewToken(currentRouteGalleryId());
}

/**
 * True while a preview session is active, or restorable in this iframe after a reload. While it is,
 * `weddingToken` (possibly the couple's own live guest session) is never used or cleared on the
 * preview's behalf.
 */
export function hasPreviewSessionToken() {
  return activePreviewToken() !== '';
}

/**
 * The persisted guest token. A preview token left in `weddingToken` by earlier builds is
 * ignored and cleared, so it can never block the couple's own live-gallery session.
 * @returns {string|null}
 */
export function getStoredGuestToken() {
  try {
    const stored = localStorage.getItem(GUEST_TOKEN_STORAGE_KEY);
    if (stored && isPreviewToken(stored)) {
      localStorage.removeItem(GUEST_TOKEN_STORAGE_KEY);
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}

/**
 * The bearer token for guest API calls: the preview token during a preview session (in memory, or
 * mirrored in `sessionStorage` right after an iframe reload), otherwise the persisted guest token.
 * A preview frame never falls back to `weddingToken`.
 * @returns {string|null}
 */
export function getGuestAuthToken() {
  return activePreviewToken() || getStoredGuestToken();
}
